import { Component, inject, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AccountSessionExpiredService } from '@app/core/libraries';
import { FixedHeightService } from '@app/shared/services';
import { HeaderComponent, BreadcrumbComponent, UploadTrayComponent } from '@app/shared/components';
import { AudioFacade } from '@app/store/audio';
import { CertificatesFacade } from '@app/store/certificates';
import { CourseFacade } from '@app/store/course';
import { CourseAssessmentFacade } from '@app/store/course-assessment';
import { ImageBlockFacade } from '@app/store/image-block';
import { ImagesFacade } from '@app/store/images';
import { LearningPortfolioFacade } from '@app/store/learning-portfolio';
import { LessonFacade } from '@app/store/lesson';
import { MaterialsFacade } from '@app/store/materials';
import { ModuleFacade } from '@app/store/module';
import { NewsFacade } from '@app/store/news';
import { NextFacade } from '@app/store/next';
import { OrganisationFacade } from '@app/store/organisation';
import { PathwayFacade } from '@app/store/pathways';
import { ProfileStatisticsFacade } from '@app/store/profile-statistics';
import { ProgressFacade } from '@app/store/progress';
import { QuizFacade } from '@app/store/quiz';
import { QuizProgressFacade } from '@app/store/quiz-progress';
import { ReflectionFacade } from '@app/store/reflection';
import { SchoolFacade } from '@app/store/school';
import { SecureUploadFacade } from '@app/store/secure-upload';
import { TextFacade } from '@app/store/text';
import { VideosFacade } from '@app/store/videos';
import { FooterComponent } from './footer/footer.component';
import { SidebarComponent } from './sidebar/sidebar.component';

@Component({
    selector: 'app-shell',
    templateUrl: './shell.component.html',
    styleUrls: ['./shell.component.css'],
    imports: [RouterModule, SidebarComponent, FooterComponent, HeaderComponent, BreadcrumbComponent, UploadTrayComponent],
    providers: [AccountSessionExpiredService],
    host: {
        '[class.fixed-height]': 'fixedHeight()',
    },
})
export class ShellComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly videosFacade = inject(VideosFacade);
    private readonly audioFacade = inject(AudioFacade);
    private readonly materialsFacade = inject(MaterialsFacade);
    private readonly imageBlockFacade = inject(ImageBlockFacade);
    private readonly textFacade = inject(TextFacade);
    private readonly pathwayFacade = inject(PathwayFacade);
    private readonly newsFacade = inject(NewsFacade);
    private readonly progressFacade = inject(ProgressFacade);
    private readonly courseFacade = inject(CourseFacade);
    private readonly courseAssessmentFacade = inject(CourseAssessmentFacade);
    private readonly moduleFacade = inject(ModuleFacade);
    private readonly lessonFacade = inject(LessonFacade);
    private readonly nextFacade = inject(NextFacade);
    private readonly organisationFacade = inject(OrganisationFacade);
    private readonly learningPortfolioFacade = inject(LearningPortfolioFacade);
    private readonly reflectionFacade = inject(ReflectionFacade);
    private readonly imagesFacade = inject(ImagesFacade);
    private readonly quizFacade = inject(QuizFacade);
    private readonly quizProgressFacade = inject(QuizProgressFacade);
    private readonly schoolFacade = inject(SchoolFacade);
    private readonly secureUploadFacade = inject(SecureUploadFacade);
    private readonly certificatesFacade = inject(CertificatesFacade);
    private readonly profileStatisticsFacade = inject(ProfileStatisticsFacade);

    readonly accountSessionExpiredService = inject(AccountSessionExpiredService);
    readonly fixedHeightService = inject(FixedHeightService);

    readonly fixedHeight = this.fixedHeightService.fixedHeight;
    readonly headerHidden = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.isHeaderHidden()),
        ),
        { initialValue: false },
    );

    ngOnDestroy(): void {
        this.videosFacade.clear();
        this.audioFacade.clear();
        this.materialsFacade.clear();
        this.imageBlockFacade.clear();
        this.textFacade.clear();
        this.pathwayFacade.clear();
        this.newsFacade.clear();
        this.progressFacade.clear();
        this.courseFacade.clear();
        this.courseAssessmentFacade.clear();
        this.moduleFacade.clear();
        this.lessonFacade.clear();
        this.nextFacade.clear();
        this.organisationFacade.clear();
        this.learningPortfolioFacade.clear();
        this.reflectionFacade.clear();
        this.imagesFacade.clear();
        this.quizFacade.clear();
        this.quizProgressFacade.clear();
        this.schoolFacade.clear();
        this.secureUploadFacade.clear();
        this.certificatesFacade.clear();
        this.profileStatisticsFacade.clear();
    }

    private isHeaderHidden(): boolean {
        let route = this.router.routerState.snapshot.root;
        let primaryChild = route.children.find((child) => child.outlet === 'primary');

        while (primaryChild) {
            route = primaryChild;
            primaryChild = route.children.find((child) => child.outlet === 'primary');
        }

        return route.data['hideHeader'] === true;
    }
}
